import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { Modal } from "./components";
import type { User } from "@kkindex/shared";
import type { Execute } from "./Editor";
import s from "./App.module.css";
export function Settings({
  user,
  execute,
  onClose,
  onLogout,
}: {
  user: User;
  execute: Execute;
  onClose: () => void;
  onLogout: () => void;
}) {
  const [timezone, setTimezone] = useState(user.timezone),
    [name, setName] = useState(user.displayName),
    [password, setPassword] = useState(""),
    [current, setCurrent] = useState(""),
    [message, setMessage] = useState("");
  return (
    <Modal title="个人设置" onClose={onClose}>
      <label>
        显示名称
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() =>
            void execute("/settings", "PATCH", { displayName: name }).catch(
              () => {},
            )
          }
        />
      </label>
      <label>
        账号时区
        <input
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          onBlur={() =>
            void execute("/settings", "PATCH", { timezone }).catch(() => {})
          }
        />
      </label>
      <p className={s.hint}>日期不会随设备时区偏移；今天和逾期按此时区计算。</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void execute("/password", "POST", { current, password })
            .then(() => {
              setCurrent("");
              setPassword("");
              setMessage("密码已修改，其他设备已退出");
            })
            .catch(() => {});
        }}
      >
        <label>
          当前密码
          <input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
        </label>
        <label>
          新密码
          <input
            type="password"
            autoComplete="new-password"
            minLength={12}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        <button className={s.primary}>修改密码</button>
      </form>
      <p>{message}</p>
      <footer>
        <button
          onClick={() => void execute("/logout", "POST", {}).then(onLogout)}
        >
          退出当前设备
        </button>
        <button
          className={s.danger}
          onClick={() => {
            if (confirm("退出所有设备的登录？"))
              void execute("/sessions/revoke", "POST", {}).then(onLogout);
          }}
        >
          退出全部设备
        </button>
      </footer>
    </Modal>
  );
}
type Status = {
  version: string;
  sqlite: { version: string };
  jobs: { name: string; status: string; detail: string; lastRun: string }[];
  holidays: { year: number; version: string; importedAt: string }[];
};
export function Admin({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState("账号"),
    [error, setError] = useState(""),
    [year, setYear] = useState(new Date().getFullYear()),
    [candidate, setCandidate] = useState<{
      data: unknown;
      source: string;
      version: string;
    } | null>(null),
    [manual, setManual] = useState("");
  const accounts = useQuery({
      queryKey: ["admin", "users"],
      queryFn: () => api<{ users: User[] }>("/admin/users"),
    }),
    status = useQuery({
      queryKey: ["admin", "status"],
      queryFn: () => api<Status>("/admin/status"),
    });
  async function run(fn: () => Promise<unknown>) {
    setError("");
    try {
      await fn();
      await accounts.refetch();
      await status.refetch();
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <Modal title="系统管理" onClose={onClose} wide>
      <nav className={s.filters}>
        {["账号", "节假日", "系统状态"].map((t) => (
          <button
            key={t}
            className={tab === t ? s.active : ""}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </nav>
      {error && (
        <p role="alert" className={s.error}>
          {error}
        </p>
      )}
      {tab === "账号" && (
        <>
          <div className={s.adminUsers}>
            {accounts.data?.users.map((u) => (
              <div className={s.adminUser} key={u.id}>
                <span>
                  {u.displayName}
                  <small>
                    {u.username} · {u.role === "admin" ? "管理员" : "用户"} ·{" "}
                    {u.active ? "已启用" : "已停用"}
                  </small>
                </span>
                <button
                  onClick={() =>
                    void run(async () => {
                      const password = prompt("新临时密码（至少 12 个字符）");
                      if (password)
                        await api(`/admin/users/${u.id}`, "PATCH", {
                          password,
                        });
                    })
                  }
                >
                  重置密码
                </button>
                <button
                  onClick={() =>
                    void run(() =>
                      api(`/admin/users/${u.id}`, "PATCH", { revoke: true }),
                    )
                  }
                >
                  撤销会话
                </button>
                <button
                  onClick={() =>
                    void run(() =>
                      api(`/admin/users/${u.id}`, "PATCH", {
                        active: !u.active,
                      }),
                    )
                  }
                >
                  {u.active ? "停用" : "启用"}
                </button>
              </div>
            ))}
          </div>
          <h3>创建账号</h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget,
                f = new FormData(form);
              void run(async () => {
                await api("/admin/users", "POST", Object.fromEntries(f));
                form.reset();
              });
            }}
          >
            <div className={s.two}>
              <label>
                用户名
                <input name="username" required minLength={3} />
              </label>
              <label>
                显示名称
                <input name="displayName" required />
              </label>
            </div>
            <div className={s.two}>
              <label>
                临时密码
                <input
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                />
              </label>
              <label>
                角色
                <select name="role">
                  <option value="user">普通用户</option>
                  <option value="admin">管理员</option>
                </select>
              </label>
            </div>
            <button className={s.primary}>创建账号</button>
          </form>
        </>
      )}
      {tab === "节假日" && (
        <>
          <div className={s.inline}>
            <input
              aria-label="节假日年份"
              type="number"
              min={1900}
              max={2100}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            />
            <button
              className={s.outline}
              onClick={() =>
                void run(async () => {
                  const result = await api<{
                    data: unknown;
                    source: string;
                    version: string;
                  }>("/admin/holidays/preview", "POST", { year });
                  setCandidate(result);
                })
              }
            >
              获取年度数据
            </button>
          </div>
          <p className={s.hint}>预览核对后导入。人工修正不会被后续导入覆盖。</p>
          <label>
            手动导入年度 JSON
            <textarea
              rows={3}
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              placeholder='{"year":2026,"region":"CN","dates":[...]}'
            />
          </label>
          <button
            onClick={() =>
              void run(async () => {
                setCandidate({
                  data: JSON.parse(manual),
                  source: "管理员手动导入",
                  version: "manual",
                });
              })
            }
          >
            预览手动数据
          </button>
          {candidate && (
            <>
              <pre className={s.jsonPreview}>
                {JSON.stringify(candidate.data, null, 2)}
              </pre>
              <button
                className={s.primary}
                onClick={() =>
                  void run(async () => {
                    await api("/admin/holidays/import", "POST", candidate);
                    setCandidate(null);
                  })
                }
              >
                确认导入
              </button>
            </>
          )}
          <h3>修正单个日期</h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              void run(() =>
                api("/admin/holidays/override", "PUT", Object.fromEntries(f)),
              );
            }}
          >
            <div className={s.two}>
              <label>
                日期
                <input name="date" type="date" required />
              </label>
              <label>
                名称
                <input name="name" required />
              </label>
            </div>
            <label>
              日期类型
              <select name="type">
                <option value="public_holiday">休息日</option>
                <option value="transfer_workday">调休工作日</option>
                <option value="normal">恢复普通周末规则</option>
              </select>
            </label>
            <button className={s.outline}>保存修正</button>
          </form>
          <label>
            移除修正，重新使用导入数据
            <input
              type="date"
              onChange={(e) => {
                const date = e.target.value;
                if (date && confirm("删除这一天的人工修正？"))
                  void run(() =>
                    api(`/admin/holidays/override/${date}`, "DELETE"),
                  );
              }}
            />
          </label>
        </>
      )}
      {tab === "系统状态" && (
        <>
          <dl className={s.statusList}>
            <dt>应用版本</dt>
            <dd>{status.data?.version}</dd>
            <dt>SQLite</dt>
            <dd>{status.data?.sqlite.version}</dd>
          </dl>
          <h3>后台任务</h3>
          {status.data?.jobs.map((j) => (
            <div key={j.name} className={s.statusRow}>
              <strong>{j.name === "backup" ? "每日备份" : "节假日检查"}</strong>
              <span>
                {j.status === "pending"
                  ? "等待管理员确认"
                  : j.status === "ok"
                    ? "正常"
                    : "失败"}
              </span>
              <small>{j.lastRun}</small>
              <p>
                {j.status === "pending"
                  ? "已发现下一年度数据，请到节假日页获取并预览。"
                  : j.detail}
              </p>
            </div>
          ))}
          <h3>已导入节假日</h3>
          {status.data?.holidays.map((h) => (
            <p key={h.year}>
              {h.year} · {h.version} · {h.importedAt.slice(0, 10)}
            </p>
          ))}
        </>
      )}
    </Modal>
  );
}
